/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faEye,
	faPen,
	faTrash,
	faArrowLeftLong,
	faArrowDownLong,
	faBan,
} from '@fortawesome/free-solid-svg-icons';
import {
	getModulesAndCategories,
	Module,
	categoryShort,
	categoryColors,
} from '../../../features/modules';
import { UserPermission } from '../../users/types/types';

// Structure d'une permission telle que retournée par l'API
type ApiPermission = UserPermission;

const rights = [
	{ key: 'read', label: 'Lecture', icon: faEye, icon2: faBan },
	{ key: 'write', label: 'Écriture', icon: faPen, icon2: faBan },
	{ key: 'delete', label: 'Suppression', icon: faTrash, icon2: faBan },
];

interface UserRightsMatrixProps {
	initialPermissions?: ApiPermission[];
	onChange?: (permissions: ApiPermission[]) => void;
}

// eslint-disable-next-line react/prop-types
const UserRightsMatrix: React.FC<UserRightsMatrixProps> = ({
	initialPermissions = [],
	onChange,
}) => {
	// State
	const [modules, setModules] = useState<Module[]>([]);
	const [permissions, setPermissions] = useState<
		Record<string, Record<string, boolean>>
	>({});

	const token = localStorage.getItem('token') || '';

	// Conversion de la matrice booléenne en tableau UserPermission[] pour le parent
	const emitChange = (
		newPermissions: Record<string, Record<string, boolean>>,
		currentModules: Module[]
	) => {
		if (!onChange) return;
		const result: ApiPermission[] = [];
		currentModules.forEach((m) => {
			const modulePerms = newPermissions[m.title] ?? {};
			rights.forEach((r) => {
				if (modulePerms[r.key]) {
					result.push({
						module: { name: m.name, title: m.title, id: m.id },
						action: r.key as ApiPermission['action'],
					});
				}
			});
		});
		onChange(result);
	};

	// Fetch des modules
	useEffect(() => {
		const fetchData = async () => {
			try {
				const { modules } = await getModulesAndCategories(token);
				setModules(modules);
			} catch (error) {
				console.error(error);
			}
		};
		fetchData();
	}, [token]);

	// Génération automatique des permissions quand modules ou initialPermissions change
	useEffect(() => {
		if (modules.length > 0) {
			const initial: Record<string, Record<string, boolean>> = {};
			modules.forEach((m) => {
				// Initialiser toutes les permissions à false par défaut
				initial[m.title] = { read: false, write: false, delete: false };
			});
			// Appliquer les permissions réelles reçues en props
			initialPermissions.forEach((perm) => {
				const moduleTitle = perm.module?.title;
				if (
					moduleTitle &&
					initial[moduleTitle] &&
					rights.some((r) => r.key === perm.action)
				) {
					initial[moduleTitle][perm.action] = true;
				}
			});
			setPermissions(initial);
		}
	}, [modules, initialPermissions]);

	const toggleRight = (module: string, right: string) => {
		setPermissions((prev) => {
			const next = {
				...prev,
				[module]: { ...prev[module], [right]: !prev[module][right] },
			};
			emitChange(next, modules);
			return next;
		});
	};

	const allCheckedForModule = (module: string) =>
		Object.values(permissions[module] || {}).every(Boolean);

	const allCheckedForRight = (right: string) =>
		modules.every((m) => permissions[m.title]?.[right]);

	const toggleAllForModule = (module: string) => {
		const checked = allCheckedForModule(module);
		setPermissions((prev) => {
			const next = {
				...prev,
				[module]: Object.fromEntries(
					Object.entries(prev[module]).map(([key]) => [key, !checked])
				),
			};
			emitChange(next, modules);
			return next;
		});
	};

	const toggleAllForRight = (right: string) => {
		const checked = allCheckedForRight(right);
		setPermissions((prev) => {
			const next = { ...prev };
			modules.forEach((m) => {
				next[m.title] = { ...next[m.title], [right]: !checked };
			});
			emitChange(next, modules);
			return next;
		});
	};

	return (
		<div className="p-6 flex justify-center">
			<div className="bg-white shadow rounded-2xl p-4 w-full max-w-5xl">
				<h2 className="text-xl font-semibold mb-4 text-center">
					Droits des utilisateurs par module
				</h2>

				{modules.length === 0 ? (
					<p className="text-center text-gray-500">Chargement des modules...</p>
				) : (
					<div className="overflow-x-auto">
						<table className="mx-auto text-sm border-collapse">
							<thead>
								<tr className="border-b text-center">
									<th className="p-2 text-left">Module</th>
									{rights.map((r) => (
										<th key={r.key} className="p-2 min-w-[80px]">
											<div className="flex flex-col items-center space-y-1">
												<span>{r.label}</span>
												<button
													type="button"
													onClick={() => toggleAllForRight(r.key)}
													className={`w-8 h-8 flex items-center justify-center rounded-full transition ${
														allCheckedForRight(r.key)
															? 'bg-green-500 text-white'
															: 'bg-gray-200 text-gray-600 hover:bg-gray-300'
													}`}
												>
													<FontAwesomeIcon icon={faArrowDownLong} />
												</button>
											</div>
										</th>
									))}
									<th className="p-2"></th>
								</tr>
							</thead>
							<tbody>
								{modules.map((m) => (
									<tr
										key={m.title}
										className="border-b hover:bg-gray-50 text-center"
									>
										<td className="p-2 text-left font-medium">
											<span
												className={`px-2 py-0.5 rounded-full text-sm font-medium mr-2 ${
													categoryColors[m.category] ||
													'bg-gray-200 text-gray-800'
												}`}
											>
												{categoryShort[m.category] || m.category}
											</span>
											{m.title}
										</td>
										{rights.map((r) => {
											const active = permissions[m.title]?.[r.key];
											return (
												<td
													key={r.key}
													className="p-2 text-center align-middle"
												>
													<div className="flex justify-center items-center">
														<button
															type="button"
															onClick={() => toggleRight(m.title, r.key)}
															className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
																active
																	? 'bg-green-500 text-white hover:bg-green-600'
																	: 'bg-gray-200 text-gray-600 hover:bg-gray-300'
															}`}
														>
															<FontAwesomeIcon
																icon={active ? r.icon : r.icon2}
															/>
														</button>
													</div>
												</td>
											);
										})}
										<td className="p-2">
											<button
												type="button"
												onClick={() => toggleAllForModule(m.title)}
												className={`w-8 h-8 flex items-center justify-center rounded-full transition ${
													allCheckedForModule(m.title)
														? 'bg-green-500 text-white'
														: 'bg-gray-200 text-gray-600 hover:bg-gray-300'
												}`}
											>
												<FontAwesomeIcon icon={faArrowLeftLong} />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default UserRightsMatrix;
